import { RENDER_IMAGE_TYPE } from '../constants/renderPreviewConstants';
import { handleDownloadEditedImage } from '../components/EditImageTool';
import { warningToast } from './toastHelper';

/**
 * Post Edit is the one downloadable pass that is not a stored file: it is the
 * render as produced with its saved edit_config drawn over it. Anything putting
 * a render's images into a zip has to draw it first, which is what this is for.
 */

// Each draw is canvas work over a full size render, so a few at a time keeps a
// large selection quick without holding every decoded image in memory at once.
const DRAW_CONCURRENCY = 4;

/** The stored file's own name, without the query string a signed URL carries. */
export const fileNameFromUrl = (url) => (url || '').split('/').pop().split('?')[0];

/** Matches what the editor encodes to: jpeg stays jpeg, everything else is png. */
export const postEditExtension = (sourceUrl) => {
  const ext = fileNameFromUrl(sourceUrl).split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg'].includes(ext) ? ext : 'png';
};

/**
 * A camera has a Post Edit pass when it carries saved edits - the same test the
 * viewer and Render Details use to decide whether to offer the pass at all.
 */
export const hasPostEditImage = (camera) => !!Object.keys(camera?.edit_config || {}).length;

/**
 * The files the editor draws from. A TIFF render can't be decoded by a canvas,
 * so the extra_* files are the ones to use - the same choice Render Details
 * makes. `otherImage` is the opposite pass, which the Filmic Mode slider blends
 * in.
 */
const postEditSources = (camera, render) => {
  const isFilmic = render?.img_type === RENDER_IMAGE_TYPE.FILMIC;
  const standard = camera?.extra_file || camera?.file;
  const filmic = camera?.extra_filmic_file || camera?.filmic_file;

  return {
    // A render marked filmic that carries no filmic file still has a standard
    // one to draw from, and the other way round - better the other pass than no
    // Post Edit image at all.
    source: (isFilmic ? filmic : standard) || standard || filmic,
    otherImage: isFilmic ? standard : filmic,
    colorPassImage: camera?.color_pass_file,
  };
};

/**
 * Draws the Post Edit image for one camera of `render`.
 *
 * Resolves to `{ url, source }` - a blob: URL the caller is responsible for
 * revoking, and the file it was drawn from so the extension can be worked out -
 * or null when the camera has no edits, has nothing to draw from, or fails.
 */
const renderPostEditImage = async (camera, render) => {
  if (!hasPostEditImage(camera)) return null;

  const { source, otherImage, colorPassImage } = postEditSources(camera, render);
  if (!source) {
    // Saved edits but no file to draw them over - the payload carries no pass
    // for this camera. Logged so the warning that follows always has a reason.
    console.error('No file to draw the Post Edit image from:', camera?.angle_name, camera?.id);
    return null;
  }

  try {
    const url = await handleDownloadEditedImage({
      url: source,
      imageToolData: camera.edit_config,
      otherImage,
      colorPassImage,
      title: camera?.angle_name,
      returnFile: true,
    });
    return url ? { url, source } : null;
  } catch (error) {
    // One angle failing to draw shouldn't cost the user the rest of the zip.
    console.error('Failed to render the Post Edit image for the zip:', error);
    return null;
  }
};

/**
 * Draws the Post Edit image for every edited camera across `renders`, a few at
 * a time so the canvas work overlaps instead of queueing up.
 *
 * Resolves to a Map of camera id -> `{ url, source }` holding only the cameras
 * that have one, so collecting the files stays a plain synchronous loop. The
 * caller owns the blob: URLs - hand the Map to `revokePostEditImages` once the
 * download is done.
 */
export const renderPostEditImages = async (renders = []) => {
  const edited = [];
  renders.forEach((render) =>
    (render?.images || []).forEach((camera) => {
      if (hasPostEditImage(camera)) edited.push({ camera, render });
    })
  );

  const drawn = new Map();
  for (let i = 0; i < edited.length; i += DRAW_CONCURRENCY) {
    const batch = edited.slice(i, i + DRAW_CONCURRENCY);
    const images = await Promise.all(
      batch.map(({ camera, render }) => renderPostEditImage(camera, render))
    );
    images.forEach((image, index) => image && drawn.set(batch[index].camera?.id, image));
  }

  // A short zip is worse than a slow one: say so rather than let the pass go
  // missing without a word.
  const failed = edited.length - drawn.size;
  if (failed) warningToast(`Couldn't prepare the Post Edit image for ${failed} image(s).`);

  return drawn;
};

/** The drawn images were only ever needed to fill the download. */
export const revokePostEditImages = (drawn) => drawn?.forEach(({ url }) => URL.revokeObjectURL(url));
